import {
    Component,
    OnInit,
    ViewEncapsulation,
    HostListener,
    ViewChildren,
    AfterViewInit,
    OnChanges,
    QueryList, SimpleChanges, ChangeDetectorRef, Input, AfterContentChecked, Output, EventEmitter, DestroyRef, inject
} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {FormlyFormOptions} from '@ngx-formly/core';
import {BehaviorSubject, combineLatestWith, delay, Observable, of} from 'rxjs';
import {
    filter,
    map,
    startWith,
    take,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';

import {TreeNode} from '@ali-hm/angular-tree-component';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {itemAttributeFieldMap as fieldMap} from '@emm/shared/formly/maps/item-attribute.config';
import {DynamicFormFieldService} from '@emm/shared/services/dynamic-form-field.service';
import {DynamicMenuFormComponent} from '@emm/shared/components/dynamic-form/dynamic-menu-form/dynamic-menu-form.component';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AttributesFormComponent} from '@emm/menu/components/menu-attributes/attributes-form/attributes-form.component';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {MenuFormFacadeService} from '@emm/menu/services/menu-form-facade.service';
import {ItemDetailStoreService} from '@emm/menu/store/item-detail/item-detail-store.service';
import {MenuFiltersFacadeService} from '@emm/menu/services/menu-filters-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {MenuDateUtil} from '@emm/core/util/menu-date.util';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {getSortedAttributes, ItemAttribute} from '@emm/core/models/item-attribute.model';
import {ItemAttributeType} from '@emm/core/models/enums';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {NotificationService} from '@emm/shared/services/notification.service';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-menu-attributes',
    styleUrls: ['./menu-attributes.component.scss'],
    templateUrl: './menu-attributes.component.html',
    providers: [DynamicFormFieldService,]
})
export class MenuAttributesComponent implements OnInit, AfterViewInit, AfterContentChecked, OnChanges {

    @Input() rightPaneWidth: string;
    @Input() nodeId: string;
    @Input() selectedLocation: string;
    @Output() submenuRenamed = new EventEmitter<boolean>();
    @ViewChildren(AttributesFormComponent) public components: QueryList<DynamicMenuFormComponent>;
    attributesForm: AttributesFormComponent;

    attributes$: Observable<any>;
    attributesByGroup = new Map<string, BehaviorSubject<any>>();
    attributeGroups;
    previousAttributeGroups$ = new BehaviorSubject<AttributeGroup[]>([]);
    countryCode: string;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    disabled$: Observable<any>;
    effectiveDate = new BehaviorSubject<string>(null);
    fields$: Observable<any>;
    fields = [];
    fieldsObservable: Observable<any>;
    first = true;
    form = new BehaviorSubject<FormGroup>(null);
    isLoading$: Observable<any> = this.formFacade.isLoading$;
    isReadOnly$: Observable<boolean> = this.authFacade.isReadOnly$;
    isSubMenu = false;
    item: any; // todo
    item$: Observable<any> = this.formFacade.selectedItemDetail$;
    itemAsNode$: Observable<any> = this.formFacade.selectedTreeNode$;
    itemNode: TreeNode;
    model$: Observable<any>;
    model = {};
    valuesObservable: Observable<any>;
    options: FormlyFormOptions = {};
    previousItem: string;
    selectedFeatureScope$ = this.treeFacade.selectedFeatureScope$;
    isGlobal: boolean;
    scope: string;
    isChecked;
    locationSet: string;
    renderForm = new BehaviorSubject<boolean>(null);
    selectedSubmenu$ = this.treeFacade.selectedSubmenu$;
    menuParamsContext$ = this.formFacade.menuParamsContext$;
    selectedLocationSetType$ = this.menuFiltersFacade.selectedLocationSetType$;
    selectedLocationSetType: string;
    language$ = new BehaviorSubject<string>('en');

    constructor(
        private authFacade: AuthFacadeService,
        public dialogService: DialogService,
        private formFacade: MenuFormFacadeService,
        private itemDetailStore: ItemDetailStoreService,
        private menuFiltersFacade: MenuFiltersFacadeService,
        private api: MenuApiService,
        private sharedFacade: SharedFacadeService,
        public treeFacade: MenuTreeFacadeService,
        private changeDetector: ChangeDetectorRef,
        private managementApi: ManagementApiService,
        private notificationService: NotificationService,
    ) {
        this.sharedFacade.localeSelected$
            .pipe(
                tap(locale => this.language$.next(locale.language)),
                withLatestFrom(this.formFacade.menuParamsContext$, this.item$, this.selectedFeatureScope$,
                    this.menuFiltersFacade.selectedVenue$),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([locale, {effectiveDate, menuKey}, item, featureScope, venue]) => {
                if (locale && locale.language && item) {
                    const {tagPath} = item;
                    this.api
                        .getMenuItemDetailByFeatureScope(featureScope, effectiveDate, menuKey, tagPath, locale.language, venue)
                        .pipe(
                            takeUntilDestroyed(this.destroyRef)
                        )
                        .subscribe({
                            next: (itemDetail) => {
                                if (!itemDetail) {
                                    return;
                                }
                                if (this.previousItem && this.previousItem !== JSON.stringify(itemDetail)) {
                                    this.itemDetailStore.dispatch.updateFromLocaleSelected(itemDetail, true);
                                }
                                this.previousItem = JSON.stringify(itemDetail);
                                this.valuesObservable = of(itemDetail.attributes);
                            },
                            error: (error) => {
                                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                            }
                        });
                }
            });
    }


    ngOnInit(): void {
        this.disabled$ = this.setDisabledSubscription();
        this.attributes$ = this.setAttributesSubscription();
        this.model$ = this.setModelSubscription();
        this.menuFiltersFacade.effectiveDate$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
            this.effectiveDate.next(MenuDateUtil.formatDateToMidnight(result));
        });

        this.selectedFeatureScope$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                this.scope = result;
        });
        this.isGlobal = this.scope === 'Global';
        this.selectedLocationSetType$.subscribe((locationSetType) => {
            this.selectedLocationSetType = locationSetType.tag;
        });

        this.setFieldsSubscription();
        this.formFacade.selectedItemDetail$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
            this.getAttributeGroups();
        })
        this.setSelectedItemDetail();

        this.menuFiltersFacade.selectedLocationSet$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(data => {
            if (data && data.countryCode) {
                this.countryCode = data.countryCode;
                this.locationSet = data.scope;
            }
        });

        this.fields$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
            for (let i = 0; i < result.length; i++) {
                const key = result[i].key;
                this.fields.push({
                    name: result[i].templateOptions.label,
                    value: result[i]['value'] ? result[i]['value'] : '',
                    key,
                    label: result[i].templateOptions.label,
                    required: result[i].templateOptions.required,
                    order: i,
                    disabled: result[i].templateOptions.readonly,
                    controlType: result[i].type,
                    type: result[i].type,
                    options: result[i].templateOptions.options,
                });
            }
            this.fieldsObservable = of(this.fields);
        });

        setInterval(() => {
            this.isGlobal = this.scope === 'Global';
        }, 8500);
    }


    ngAfterViewInit() {
        this.item$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((item) => {
            this.changeDetector.detectChanges();
        });
        this.components.changes
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
            (comps: QueryList<AttributesFormComponent>) => {
                this.attributesForm = comps.first;
                this.changeDetector.detectChanges();
            }
        );

        this.itemAsNode$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                delay(0))
            .subscribe((itemAsNode) => {
                this.itemNode = itemAsNode;
                this.isSubMenu = itemAsNode?.parent?.parent === null;
            });
    }


    ngAfterContentChecked() {
        this.changeDetector.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges) {}


    setDisabledSubscription(): Observable<any> {
        return this.isReadOnly$.pipe(
            combineLatestWith(this.isLoading$),
            map((sources: [boolean, Observable<boolean>]) => R.any(R.equals(true), sources))
        );
    }


    setAttributesSubscription(): Observable<any> {
        return this.item$.pipe(
            filter(RA.isNotNil),
            startWith([] as ItemAttribute[]),
            map((item: ItemDetail) => item.attributes)
        );
    }


    setModelSubscription(): Observable<any> {
        return this.attributes$.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(RA.isNonEmptyArray),
            map((attributes: ItemAttribute[]) => {
                return attributes.reduce((acc, cur) => {
                    const isBoolean = RA.isBoolean(cur.value);
                    return {
                        ...acc,
                        [cur.jsonProperty]: isBoolean ? String(cur.value) : cur.value
                    };
                }, {});
            }),
            tap(model => this.model = model)
        );
    }


    setFieldsSubscription(): void {
        this.fields$ = this.attributes$.pipe(
            tap(() => {
                if (this.options && this.options.resetModel && this.options.updateInitialValue) {
                    this.options.resetModel();
                    this.options.updateInitialValue();
                }
            }),
            filter(RA.isNonEmptyArray),
            withLatestFrom(this.isReadOnly$, this.sharedFacade.localeSelected$),
            map(([attributes, isReadOnly, localeSelected]) => {
                if (!localeSelected) {
                    return [];
                }
                return getSortedAttributes<ItemAttribute>(attributes)
                    .filter(({type}) => Object.values(ItemAttributeType).includes(type))
                    .filter(({type}) => Object.keys(fieldMap).includes(type))
                    .map((attribute: ItemAttribute, index: number) => {
                        const mapper = fieldMap[attribute.type];
                        return {
                            config: mapper(attribute, index, !this.isGlobal, localeSelected.language),
                            attribute
                        };
                    })
                    .map(({config, attribute}) => {
                        return {
                            ...config,
                            templateOptions: {
                                ...config.templateOptions,
                                showLocaleIcons: false,
                                readonly: !this.isGlobal,
                            }
                        };
                    });
            }),
            takeUntilDestroyed(this.destroyRef)
        );
    }


    setSelectedItemDetail(): void {
        // todo: consider moving this to the facade
        this.formFacade.selectedItemDetail$
            .pipe(
                tap(() => this.options = {}),
                filter(R.isNil),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((_) => this.renderForm.next(true));
    }


    getAttributeGroups(): void {
        this.managementApi.getAllAttributeGroups()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    if (this.scope === 'Viewer' && this.selectedLocationSetType === 'LOCATION') {
                        res.unshift({
                            id: 'PRICING',
                            name: 'Pricing',
                            description: 'Sales Channel Pricing'
                        });
                        this.renderForm.next(true);
                    }
                    this.attributeGroups = res;
                    res.forEach(group => {
                        this.attributesByGroup.set(group.id, this.formFacade.filterByAttributeGroup(group.id));
                    });
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    onAttributeGroupSelected($event) {
        this.previousAttributeGroups$.next($event);
    }

    onSubmenuRenamed($event: boolean) {
        this.submenuRenamed.emit($event);
    }
}
